import { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '@/lib/firebase/config';
import { signOut as firebaseSignOut, signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { UserRoles } from '@/lib/firebase/auth';
import { identifyUser, resetUser } from '@/lib/analytics/posthog';

interface AuthContextType {
  user: any;
  loading: boolean;
  userRoles: UserRoles | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  userRoles: null,
  signIn: async () => { },
  signOut: async () => { },
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<UserRoles | null>(null);
  const [hasSeenCreatorWelcome, setHasSeenCreatorWelcome] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let unsubscribeFromUserDoc: (() => void) | null = null;

    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        // Tell the Control Center chat widget who this is, so it never asks a
        // signed-in member for an address we already have. Deliberately NOT
        // folded into the analytics identify below: that one is gated on cookie
        // consent, and someone who declined analytics should still not be made
        // to retype their email to get a reply. Guarded because the widget is a
        // deferred script and this listener usually wins the race — it drains
        // _pending on boot.
        try {
          const cc = ((window as unknown as { ccChat?: Record<string, unknown> }).ccChat ??= {}) as {
            identify?: (w: unknown) => void;
            _pending?: unknown;
          };
          if (authUser.email) {
            const who = { email: authUser.email, name: authUser.displayName ?? undefined };
            if (cc.identify) cc.identify(who);
            else cc._pending = who;
          }
        } catch {
          /* the chat is a nicety; never let it break auth */
        }
        // Identify BEFORE the Firestore listener, not inside it. Both siblings
        // (Devotion Fund, The Sport Fund) do it here, and for the same reason:
        // every path below this line can fail — the user doc can be denied by
        // rules, the snapshot can error, the whole setup can throw — and on any
        // of those the person stayed anonymous while signed in. The name and
        // roles are set later as person properties; this is the account id, and
        // it is the only thing that joins what they do to who they are.
        identifyUser(authUser.uid, { email: authUser.email ?? undefined });
        try {
          // Set up real-time listener for user document
          unsubscribeFromUserDoc = onSnapshot(
            doc(db, 'users', authUser.uid),
            (userDoc) => {
              if (userDoc.exists()) {
                const userData = userDoc.data();
                const roles = userData.roles as UserRoles;

                // The auth email changes out-of-band (verifyBeforeUpdateEmail
                // link); keep the doc the mailers read in step with it.
                if (authUser.email && authUser.emailVerified && userData.email !== authUser.email) {
                  updateDoc(doc(db, 'users', authUser.uid), { email: authUser.email }).catch(() => {});
                }

                // Merge Firestore data with Firebase Auth user
                const enrichedUser = {
                  ...authUser,
                  firstName: userData.firstName,
                  lastName: userData.lastName,
                  displayName: userData.displayName || authUser.displayName,
                  photoURL: userData.photoURL || authUser.photoURL,
                };

                setUser(enrichedUser);
                setUserRoles(roles);
                identifyUser(authUser.uid, {
                  email: authUser.email,
                  name: enrichedUser.displayName || undefined,
                  is_creator: !!roles?.creator,
                });
                setHasSeenCreatorWelcome(!!userData.hasSeenCreatorWelcome);

                // Only redirect on auth pages
                const isAuthPage = location.pathname === '/signin' || location.pathname === '/signup' || location.pathname === '/signup-creator';
                if (isAuthPage) {
                  if (roles.creator) {
                    navigate('/creator');
                  } else {
                    navigate('/dashboard');
                  }
                }
              } else {
                // Document doesn't exist yet (during signup), just use auth user
                identifyUser(authUser.uid, { email: authUser.email });
                setUser(authUser);
                setUserRoles(null);
              }
              setLoading(false);
            },
            (error) => {
              console.error('Error listening to user document:', error);
              // On error, still set the auth user and continue
              setUser(authUser);
              setUserRoles(null);
              setLoading(false);
            }
          );
        } catch (error) {
          console.error('Error setting up user listener:', error);
          setUser(authUser);
          setLoading(false);
        }
      } else {
        setUser(null);
        setUserRoles(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (unsubscribeFromUserDoc) {
        unsubscribeFromUserDoc();
      }
    };
  }, [navigate, location]);

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      resetUser();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, userRoles, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);